// frontend/src/settings/InstructionNotificationSettings.js
// 指導症例通知の組織別設定。
// settings テーブルに以下のキーで保存：
//   - instruction_notification_auto_enabled    : 自動通知 ON/OFF
//   - instruction_notification_manual_enabled  : 手動完了ボタン表示 ON/OFF

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import Header from '../components/ui/Header';
import { Bell, ArrowLeft } from 'lucide-react';

const SETTING_KEYS = {
  AUTO:   'instruction_notification_auto_enabled',
  MANUAL: 'instruction_notification_manual_enabled',
};

export default function InstructionNotificationSettings() {
  const { currentOrganization } = useOrganization();
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [manualEnabled, setManualEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!currentOrganization?.id) return;
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('settings')
          .select('key, value')
          .eq('organization_id', currentOrganization.id)
          .in('key', [SETTING_KEYS.AUTO, SETTING_KEYS.MANUAL]);

        if (error) throw error;
        const map = {};
        (data || []).forEach(row => { map[row.key] = row.value; });

        // デフォルト：両方 ON
        setAutoEnabled(map[SETTING_KEYS.AUTO] !== false);
        setManualEnabled(map[SETTING_KEYS.MANUAL] !== false);
      } catch (e) {
        console.error('設定取得エラー:', e);
        setMessage('設定の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [currentOrganization?.id]);

  const handleSave = async () => {
    if (!currentOrganization?.id) return;
    setSaving(true);
    setMessage('');
    try {
      const rows = [
        {
          organization_id: currentOrganization.id,
          key: SETTING_KEYS.AUTO,
          value: autoEnabled,
          updated_at: new Date().toISOString(),
        },
        {
          organization_id: currentOrganization.id,
          key: SETTING_KEYS.MANUAL,
          value: manualEnabled,
          updated_at: new Date().toISOString(),
        },
      ];

      const { error } = await supabase
        .from('settings')
        .upsert(rows, { onConflict: 'organization_id,key' });

      if (error) throw error;
      setMessage('✅ 保存しました');
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      console.error('保存エラー:', e);
      setMessage('❌ 保存に失敗しました: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <Header />
      <div style={styles.content}>
        <Link to="/settings" style={styles.backLink}>
          <ArrowLeft size={16} style={{ display: 'inline', marginRight: 4 }} />
          設定に戻る
        </Link>

        <header style={styles.header}>
          <div style={styles.titleRow}>
            <Bell size={28} style={{ color: '#6366f1' }} />
            <h1 style={styles.title}>指導通知設定</h1>
          </div>
          <p style={styles.subtitle}>
            手術終了後、指導者に経験録の入力を促す通知の挙動を設定します。
          </p>
        </header>

        {loading ? (
          <div style={styles.loading}>読み込み中...</div>
        ) : (
          <div style={styles.card}>
            <div style={styles.row}>
              <div style={styles.rowMain}>
                <h3 style={styles.rowTitle}>自動通知</h3>
                <p style={styles.rowDescription}>
                  手術の終了時刻を過ぎたら、自動的に該当する指導者へ経験録更新の通知を送ります。
                </p>
              </div>
              <label style={styles.switch}>
                <input
                  type="checkbox"
                  checked={autoEnabled}
                  onChange={e => setAutoEnabled(e.target.checked)}
                  style={styles.switchInput}
                />
                <span style={{
                  ...styles.switchSlider,
                  ...(autoEnabled ? styles.switchSliderOn : {})
                }}>
                  <span style={{
                    ...styles.switchKnob,
                    ...(autoEnabled ? styles.switchKnobOn : {})
                  }} />
                </span>
              </label>
            </div>

            <div style={styles.divider} />

            <div style={styles.row}>
              <div style={styles.rowMain}>
                <h3 style={styles.rowTitle}>手動完了ボタンの表示</h3>
                <p style={styles.rowDescription}>
                  各手術カードに「完了」ボタンを表示します。押すと即座に通知が生成されます（自動通知より早く通知できます）。
                </p>
              </div>
              <label style={styles.switch}>
                <input
                  type="checkbox"
                  checked={manualEnabled}
                  onChange={e => setManualEnabled(e.target.checked)}
                  style={styles.switchInput}
                />
                <span style={{
                  ...styles.switchSlider,
                  ...(manualEnabled ? styles.switchSliderOn : {})
                }}>
                  <span style={{
                    ...styles.switchKnob,
                    ...(manualEnabled ? styles.switchKnobOn : {})
                  }} />
                </span>
              </label>
            </div>

            <div style={styles.note}>
              通知が生成されるのは <strong>「器械出し or 外回りに、指導者と育成対象が各 1 人以上配置された手術」</strong> のみです。配置時の役割選択（通常 / 指導者 / 育成対象）に基づいて自動判定されます。
            </div>

            <div style={styles.buttonRow}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  ...styles.button,
                  ...(saving ? styles.buttonDisabled : {})
                }}
              >
                {saving ? '保存中...' : '保存'}
              </button>
              {message && <span style={styles.message}>{message}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f3f4f6', paddingTop: 80, paddingBottom: '2rem' },
  content: { maxWidth: 800, margin: '0 auto', padding: '0 2rem' },
  backLink: { display: 'inline-block', marginBottom: '2rem', color: '#3b82f6', textDecoration: 'none', fontSize: '0.95rem' },
  header: { marginBottom: '2rem' },
  titleRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  title: { fontSize: '1.75rem', fontWeight: 'bold', color: '#1f2937', margin: 0 },
  subtitle: { fontSize: '0.95rem', color: '#6b7280', margin: 0 },
  loading: { padding: '3rem', textAlign: 'center', color: '#6b7280' },
  card: { background: '#fff', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', padding: '1rem 0' },
  rowMain: { flex: 1 },
  rowTitle: { fontSize: '1.05rem', fontWeight: 600, color: '#1f2937', margin: '0 0 0.4rem 0' },
  rowDescription: { fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: 1.6 },
  divider: { height: 1, background: '#e5e7eb', margin: '0.5rem 0' },
  switch: { position: 'relative', display: 'inline-block', width: 50, height: 28, flexShrink: 0, cursor: 'pointer' },
  switchInput: { opacity: 0, width: 0, height: 0 },
  switchSlider: { position: 'absolute', inset: 0, background: '#cbd5e1', borderRadius: 14, transition: 'background 0.2s' },
  switchSliderOn: { background: '#6366f1' },
  switchKnob: { position: 'absolute', top: 3, left: 3, width: 22, height: 22, background: '#fff', borderRadius: '50%', transition: 'transform 0.2s' },
  switchKnobOn: { transform: 'translateX(22px)' },
  note: { background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '0.85rem 1rem', fontSize: '0.85rem', color: '#92400e', lineHeight: 1.6, marginTop: '1rem' },
  buttonRow: { display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem' },
  button: { padding: '0.7rem 1.5rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' },
  buttonDisabled: { background: '#9ca3af', cursor: 'not-allowed' },
  message: { fontSize: '0.9rem', color: '#1f2937' },
};
